import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Preorder Pairings Component
 * Handles add-to-cart functionality for paired products.
 *
 * @extends {Component}
 */
class PreorderPairingsComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Event delegation for add buttons
    this.addEventListener('click', this.#handleClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles click events
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('.preorder-pairings__add-btn');
    if (button) {
      event.preventDefault();
      this.#handleAddToCart(/** @type {HTMLButtonElement} */ (button));
    }
  };

  /**
   * Handles add to cart
   * @param {HTMLButtonElement} button
   */
  async #handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const pickupValue = button.dataset.pickupValue;

    if (!variantId || button.dataset.adding === 'true') return;

    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.dataset.adding = 'true';

    try {
      // Create FormData like the native product form does
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Add pickup date line item property if available
      if (pickupValue) {
        formData.append('properties[Pickup]', pickupValue);
      }

      // Add sections for cart drawer updates
      const sections = this.#getSectionsToRender();
      if (sections.length > 0) {
        formData.append('sections', sections.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      // Check for error response (Shopify returns status field on error)
      if (data.status) {
        throw new Error(data.message || data.description || 'Failed to add to cart');
      }

      // Update button state
      button.textContent = 'Added!';
      button.dataset.adding = 'false';
      button.dataset.added = 'true';

      // Dispatch cart update event for other components
      this.dispatchEvent(
        new CartAddEvent(data, `preorder-pairings-${variantId}`, {
          source: 'preorder-pairings',
          variantId: variantId,
          sections: data.sections,
          itemCount: 1,
        })
      );

      // Open cart drawer if available
      this.#openCartDrawer(data);

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.removeAttribute('data-added');
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);

      button.textContent = 'Error';
      button.dataset.adding = 'false';

      // Dispatch error event
      this.dispatchEvent(
        new CartErrorEvent(`preorder-pairings-${variantId}`, error.message, error.message, {})
      );

      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Gets sections to render for cart update
   * @returns {string[]}
   */
  #getSectionsToRender() {
    const sections = [];

    // Check for cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const sectionEl = cartDrawer.closest('[id^="shopify-section-"]');
      if (sectionEl) {
        sections.push(sectionEl.id.replace('shopify-section-', ''));
      }
    }

    // Check for cart items components (like in cart page)
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.push(item.dataset.sectionId);
      }
    });

    return sections;
  }

  /**
   * Opens the cart drawer if available
   * @param {Object} data - Cart response data
   */
  #openCartDrawer(data) {
    const cartDrawer = /** @type {HTMLElement & { open?: () => void, renderContents?: (data: Object) => void }} */ (
      document.querySelector('cart-drawer')
    );

    if (!cartDrawer) return;

    // Use cart drawer's renderContents if available
    if (typeof cartDrawer.renderContents === 'function' && data.sections) {
      cartDrawer.renderContents(data);
    }

    // Open the drawer
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback: set open attribute
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('is-open');
    }
  }
}

if (!customElements.get('preorder-pairings-component')) {
  customElements.define('preorder-pairings-component', PreorderPairingsComponent);
}
